import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChatCircleDots, PaperPlaneRight, X, Robot } from "@phosphor-icons/react";
import { useI18n } from "../i18n/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = localStorage.getItem("chat_session_id");
  if (!sid) {
    sid = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("chat_session_id", sid);
  }
  return sid;
}

export default function Chatbot() {
  const { t, isRTL } = useI18n();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([{ role: "assistant", content: t.chatbot.greeting }]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = getSessionId();

  useEffect(() => {
    setMessages((m) =>
      m.length === 1 && m[0].role === "assistant"
        ? [{ role: "assistant", content: t.chatbot.greeting }]
        : m
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.chatbot.greeting]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  const doSend = async (text) => {
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I couldn't reach the server. Please try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    doSend(text);
  };

  const onChip = (chip) => {
    const canned = t.chatbot.chipReplies?.[chip];
    const next = [...messages, { role: "user", content: chip }];
    if (canned) {
      setMessages([...next, { role: "assistant", content: canned }]);
    } else {
      setMessages(next);
      doSend(chip);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        data-testid="chatbot-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open chatbot"
        className={`fixed bottom-6 ${isRTL ? "left-6" : "right-6"} z-40 w-14 h-14 rounded-full text-white flex items-center justify-center shadow-xl transition hover:brightness-110`}
        style={{ background: "#0F2A6E" }}
      >
        {open ? <X size={22} weight="bold" /> : <ChatCircleDots size={26} weight="fill" />}
      </button>

      {open && (
        <div
          data-testid="chatbot-panel"
          className={`fixed bottom-[92px] ${isRTL ? "left-6" : "right-6"} z-40 w-[92vw] max-w-[360px] h-[520px] max-h-[75vh] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden`}
        >
          <div className="px-5 py-4 flex items-center gap-3 text-white" style={{ background: "#0F2A6E" }}>
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
              <Robot size={18} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-sm">{t.chatbot.title}</div>
              <div className="text-xs opacity-80">{t.chatbot.subtitle}</div>
            </div>
            <button
              data-testid="chatbot-close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center"
            >
              <X size={16} weight="bold" />
            </button>
          </div>

          <div ref={scrollRef} data-testid="chatbot-messages" className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "#F5F7FA" }}>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "text-white rounded-br-sm"
                      : "bg-white border border-slate-200 text-[#1C2B3A] rounded-bl-sm"
                  }`}
                  style={m.role === "user" ? { background: "#0F2A6E" } : {}}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-2.5 rounded-2xl text-sm bg-white border border-slate-200 text-[#6B7A8D]">
                  {t.chatbot.thinking}
                </div>
              </div>
            )}
          </div>

          {/* Quick reply chips */}
          <div className="px-3 pt-3 flex flex-wrap gap-2 border-t border-slate-200 bg-white">
            {t.chatbot.chips.map((chip, i) => (
              <button
                key={i}
                data-testid={`chatbot-chip-${i}`}
                onClick={() => onChip(chip)}
                className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-[#1C2B3A] hover:border-[#1B6FE0] hover:text-[#1B6FE0] transition"
              >
                {chip}
              </button>
            ))}
          </div>

          <div className="p-3 flex items-center gap-2 bg-white">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={t.chatbot.placeholder}
              className="flex-1 h-10 px-3 rounded-md bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:border-[#1B6FE0]"
            />
            <button
              data-testid="chatbot-send"
              onClick={send}
              disabled={loading}
              aria-label={t.chatbot.send}
              className="w-10 h-10 rounded-md text-white flex items-center justify-center disabled:opacity-60 hover:brightness-110"
              style={{ background: "#1B6FE0" }}
            >
              <PaperPlaneRight size={18} weight="bold" className={isRTL ? "rtl-flip" : ""} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
